'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import type { BlogPostDetail } from '@mentorivo/shared';

import { RiArrowLeftSLine, RiArrowRightSLine } from '@/components/ui/rtl-icons';
import { Link } from '@/i18n/navigation';
import { blogPostHref } from '@/lib/blog-api';
import { formatDate } from '@/lib/format';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * Yazının altı — ilgili yazılar ve kronolojik komşular.
 *
 * ── Neden iki ayrı liste ──────────────────────────────────────────────────
 *
 * İkisi farklı soruya cevap veriyor ve biri ötekinin yerine geçmiyor:
 *
 *   • **İlgili yazılar** — "bu konuyu daha derin okumak istiyorum". Aynı
 *     konudan, en fazla üç yazı; sıralaması yeniden eskiye.
 *   • **Önceki / sonraki** — "sırayla okuyorum". Konudan BAĞIMSIZ, yayın
 *     sırasına göre. Aynı konuda başka yazı olmayabilir ama dizide her yazının
 *     bir öncesi ve bir sonrası vardır.
 *
 * İkisi de SUNUCUDA hesaplanıyor (`blog.service.ts`): arayüz dizini ikinci kez
 * çekmiyor.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Kartlar dizindeki hücrelerle aynı deriyi taşıyor — aynı içerik türü, aynı
 * yüzey.
 *
 * Giriş hareketi YOK (kullanıcı kararı, 9 Eyl 2026): bölüm sayfa açılır
 * açılmaz son hâlinde basılıyor (`initial={false}`). Eskiden `whileInView` +
 * `once: true` ile kaydırdıkça açılıyordu; site sahipleri sayfanın yüklenmiş
 * gelmesini istedi. Variant'lar yerinde — geri istenirse tetik tek satır.
 *
 * ── Zemin ─────────────────────────────────────────────────────────────────
 *
 * `--gray-25`: gövdenin beyazından ayrılıyor ama koyu bir blok değil. Sayfanın
 * tek koyu yüzeyi yine kapanış şeridi (`cta.tsx`) — /blog dizinindeki ritmin
 * aynısı.
 */

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const itemV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Hat soldan çekiliyor — dizindeki `.posts-rule` ile aynı yön ve aynı gerekçe:
   `border` animasyonlanamaz, ayrı bir öğenin `scaleX`i animasyonlanır. */
const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: { opacity: 1, scaleX: 1, transition: { duration: dur.base, ease: ease.out } },
};

export function BlogArticleFoot({ post }: { post: BlogPostDetail }) {
  const t = useTranslations('blog');
  const locale = useLocale();
  const uid = useId();

  const hasNeighbours = post.prev !== null || post.next !== null;

  // İki liste de boşsa bölümün kendisi basılmıyor: boş bir gri şerit,
  // sayfanın bittiğini değil bir şeyin eksik kaldığını söylerdi.
  if (post.related.length === 0 && !hasNeighbours) return null;

  return (
    /* Bölümün erişilebilir adı: başlık VARSA ona bağlanıyor, yoksa (yalnızca
       önceki/sonraki basıldığında) düz metinle veriliyor. `aria-labelledby`
       her durumda yazılsaydı, başlığın basılmadığı hâlde var olmayan bir
       kimliğe işaret eder ve bölüm adsız kalırdı. */
    <section
      className="artfoot"
      {...(post.related.length > 0
        ? { 'aria-labelledby': `${uid}-title` }
        : { 'aria-label': t('post.pager') })}
    >
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="artfoot-frame" variants={sectionV} initial={false} animate="show">
            {post.related.length > 0 ? (
              <>
                <motion.h2 className="artfoot-title" id={`${uid}-title`} variants={itemV}>
                  {t('post.related')}
                </motion.h2>

                <ul className="artfoot-grid">
                  {post.related.map((other) => (
                    <motion.li className="artfoot-cell" key={other.slug} variants={itemV}>
                      <motion.span className="artfoot-rule" aria-hidden="true" variants={ruleV} />

                      <p className="artfoot-topic">{t(`topics.${other.topic}`)}</p>

                      <h3 className="artfoot-name">
                        {/* Bağlantı başlığın içinde, tıklama alanı hücrenin
                            tamamı (::after) — dizindeki kartla aynı kurulum. */}
                        <Link className="artfoot-link" href={blogPostHref(other.slug)}>
                          {other.title}
                        </Link>
                      </h3>

                      <p className="artfoot-meta">
                        <time dateTime={other.publishedAt}>
                          {formatDate(other.publishedAt, locale)}
                        </time>
                        <span aria-hidden="true">·</span>
                        <span>{t('list.read', { minutes: other.readMinutes })}</span>
                      </p>
                    </motion.li>
                  ))}
                </ul>
              </>
            ) : null}

            {hasNeighbours ? (
              /* `<nav>` çünkü gerçekten gezinme: ekran okuyucu kullanan biri
                 bunu yer imi listesinden bulabiliyor. Etiketi olmadan "nav"
                 diye duyurulurdu — sayfada birden fazla nav var. */
              <motion.nav className="artfoot-pager" aria-label={t('post.pager')} variants={itemV}>
                {post.prev ? (
                  <Link className="artfoot-step" href={blogPostHref(post.prev.slug)} rel="prev">
                    <span className="artfoot-step-label">
                      <RiArrowLeftSLine size={16} aria-hidden="true" />
                      {t('post.prev')}
                    </span>
                    <span className="artfoot-step-name">{post.prev.title}</span>
                  </Link>
                ) : (
                  /* Boş hücre: tek komşu varsa öteki taraf çökmesin, "sonraki"
                     ızgaranın sağ sütununda kalsın. `aria-hidden` — ekran
                     okuyucuya boş bir bağlantı gibi görünmemeli. */
                  <span className="artfoot-step artfoot-step--empty" aria-hidden="true" />
                )}

                {post.next ? (
                  <Link
                    className="artfoot-step artfoot-step--next"
                    href={blogPostHref(post.next.slug)}
                    rel="next"
                  >
                    <span className="artfoot-step-label">
                      {t('post.next')}
                      <RiArrowRightSLine size={16} aria-hidden="true" />
                    </span>
                    <span className="artfoot-step-name">{post.next.title}</span>
                  </Link>
                ) : (
                  <span className="artfoot-step artfoot-step--empty" aria-hidden="true" />
                )}
              </motion.nav>
            ) : null}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
